import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { ArrowRight, Trophy, Flame, BarChart3, Clock } from "lucide-react";

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const map = {
    live: { c: "bg-court-red text-white animate-pulse-orange", t: t("common.live") },
    overtime: { c: "bg-orange text-white", t: t("game.overtime") },
    finished: { c: "bg-navy-surface text-ink-secondary border border-navy-border", t: t("common.final") },
    scheduled: { c: "bg-navy-surface text-ink-secondary border border-navy-border", t: t("common.upcoming") },
  };
  const s = map[status] || map.scheduled;
  return (
    <span className={`px-2 py-1 text-[10px] font-heading uppercase tracking-[0.2em] rounded-sm ${s.c}`}>
      {s.t}
    </span>
  );
}

function GameCard({ game, teams }) {
  const ta = teams[game.team_a_id];
  const tb = teams[game.team_b_id];
  return (
    <Link
      to={`/games/${game.id}`}
      className="group block bg-navy-deep border border-navy-border hover:border-orange transition-all rounded-sm overflow-hidden"
      data-testid={`game-card-${game.id}`}
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-navy-border bg-navy-surface/40">
        <StatusBadge status={game.status} />
        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{game.date}</span>
      </div>
      <div className="p-5 space-y-3">
        <Row team={ta} score={game.score_a} highlight={game.status !== "scheduled" && game.score_a >= game.score_b} />
        <Row team={tb} score={game.score_b} highlight={game.status !== "scheduled" && game.score_b > game.score_a} />
      </div>
      <div className="px-5 pb-4 flex items-center justify-between text-xs">
        <span className="font-mono uppercase tracking-widest text-ink-muted">3×3 · {game.overtime ? "OT" : "10 min"}</span>
        <span className="flex items-center gap-1 text-orange font-heading uppercase tracking-widest group-hover:translate-x-1 transition-transform">
          Box <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}

function Row({ team, score, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
          {team?.logo_url ? (
            <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="font-heading font-black text-orange text-sm">{team?.name?.[0] || "?"}</span>
          )}
        </div>
        <span className={`font-heading uppercase tracking-tight truncate ${highlight ? "text-white" : "text-ink-secondary"}`}>
          {team?.name || "TBD"}
        </span>
      </div>
      <span className={`font-mono tabular text-2xl ${highlight ? "text-orange font-bold" : "text-ink-secondary"}`}>
        {score ?? 0}
      </span>
    </div>
  );
}

export default function PublicHome() {
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [teams, setTeams] = useState({});
  const [leaders, setLeaders] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/games"),
      api.get("/tournaments"),
      api.get("/teams"),
      api.get("/leaders?metric=pts&limit=5"),
      api.get("/site/settings"),
    ]).then(([g, t, te, l, s]) => {
      setGames(g.data);
      setTournaments(t.data);
      setTeams(Object.fromEntries(te.data.map((x) => [x.id, x])));
      setLeaders(l.data);
      setSettings(s.data);
    }).catch(() => {});
  }, []);

  const live = games.filter((g) => g.status === "live" || g.status === "overtime");
  const recent = games.filter((g) => g.status === "finished").slice(0, 6);
  const upcoming = games.filter((g) => g.status === "scheduled").slice(0, 6);

  // Build featured tournament buttons (from settings; fall back to first N active)
  const tourneyMap = Object.fromEntries(tournaments.map((t) => [t.id, t]));
  let featured = (settings?.featured_tournaments || [])
    .map((f) => ({ ...f, tournament: tourneyMap[f.tournament_id] }))
    .filter((f) => f.tournament && !f.tournament.archived)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (featured.length === 0) {
    featured = tournaments.filter((t) => !t.archived).slice(0, 6).map((t, i) => ({ tournament: t, color: null, order: i }));
  }
  const blocks = settings?.visible_blocks || {};
  const show = (k) => blocks[k] !== false; // default visible

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-navy-border">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              `url(${(settings?.hero_bg_url) || "https://images.unsplash.com/photo-1519861531473-9200262188bf?q=80&w=2400&auto=format&fit=crop"})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
        <div className="absolute inset-0 court-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-28">
          <div className="inline-flex items-center gap-2 bg-orange/15 border border-orange/40 px-3 py-1 rounded-sm mb-6">
            <Flame size={14} className="text-orange" />
            <span className="text-orange font-heading uppercase tracking-[0.3em] text-[10px] md:text-xs">{settings?.hero_eyebrow || t("home.hero_eyebrow")}</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight text-white max-w-4xl leading-[1.05]">
            {settings?.hero_title_1 || "Every shot."}<br />
            {settings?.hero_title_2 || "Every foul."}<br />
            <span className="text-orange">{settings?.hero_title_3 || "Every second."}</span>
          </h1>
          <p className="text-ink-secondary text-base md:text-lg max-w-2xl mt-6">
            {settings?.hero_subtitle || "GPS STATS is the operator console & public portal for professional 3×3 basketball — real-time scoring, automatic calculations and official FIBA-style reports."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/games" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-[0.2em] px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="hero-cta-games">
              {settings?.cta_primary || "See live games"} <ArrowRight size={16} />
            </Link>
            <Link to="/leaders" className="bg-transparent border border-navy-border hover:border-white text-white font-heading uppercase tracking-[0.2em] px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="hero-cta-leaders">
              {settings?.cta_secondary || "Leaderboards"}
            </Link>
          </div>
        </div>
      </section>

      {/* Featured tournaments — quick access buttons */}
      {show("featured_tournaments") && featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10" data-testid="featured-tournaments">
          <div className="text-orange text-xs font-heading uppercase tracking-[0.3em] mb-3">{t("home.tournaments_label")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {featured.map(({ tournament, color }) => {
              const bg = color || "#DC2626";
              return (
                <Link
                  key={tournament.id}
                  to={`/t/${tournament.slug || tournament.id}`}
                  data-testid={`featured-tournament-${tournament.id}`}
                  className="group relative aspect-square rounded-sm overflow-hidden border-2 hover:scale-[1.02] transition-all flex flex-col items-center justify-center text-center p-3"
                  style={{ borderColor: bg, background: `${bg}22` }}
                >
                  <div className="w-14 h-14 rounded-sm bg-navy-deep border border-navy-border grid place-items-center overflow-hidden mb-2">
                    {tournament.logo_url ? (
                      <img src={fileUrl(tournament.logo_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-heading font-black text-2xl" style={{ color: bg }}>{tournament.name?.[0]}</span>
                    )}
                  </div>
                  <div className="font-heading font-black uppercase tracking-tight text-white text-sm md:text-base line-clamp-2">{tournament.name}</div>
                  {tournament.season && <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-1">{tournament.season}</div>}
                  <div className="mt-2 text-[10px] font-heading uppercase tracking-widest group-hover:translate-x-1 transition-transform" style={{ color: bg }}>
                    Ver →
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Live ticker */}
      {show("live_ticker") && live.length > 0 && (
        <section className="bg-court-red/10 border-b border-court-red/30">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4 overflow-x-auto">
            <span className="px-2 py-1 text-[10px] font-heading uppercase tracking-[0.3em] bg-court-red text-white animate-pulse rounded-sm shrink-0">{t("home.live_now")}</span>
            {live.map((g) => (
              <Link key={g.id} to={`/games/${g.id}`} className="text-sm font-mono tabular whitespace-nowrap text-white hover:text-orange">
                {teams[g.team_a_id]?.name || "A"} <span className="text-orange">{g.score_a}</span> – <span className="text-orange">{g.score_b}</span> {teams[g.team_b_id]?.name || "B"}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-12">
          {show("upcoming") && upcoming.length > 0 && (
            <div>
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-orange text-xs font-heading uppercase tracking-[0.3em]">{t("home.on_the_schedule")}</div>
                  <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight">{t("home.upcoming")}</h2>
                </div>
                <Link to="/games" className="text-ink-secondary text-xs uppercase tracking-widest font-heading hover:text-orange">{t("home.view_all")} →</Link>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {upcoming.map((g) => <GameCard key={g.id} game={g} teams={teams} />)}
              </div>
            </div>
          )}

          {show("latest_results") && (
            <div>
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="text-orange text-xs font-heading uppercase tracking-[0.3em]">{t("home.just_played")}</div>
                  <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight">{t("home.latest_results")}</h2>
                </div>
                <Link to="/games" className="text-ink-secondary text-xs uppercase tracking-widest font-heading hover:text-orange">{t("home.view_all")} →</Link>
              </div>
              {recent.length === 0 ? (
                <div className="border border-dashed border-navy-border p-10 text-center text-ink-muted text-sm">{t("home.no_finished")}</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {recent.map((g) => <GameCard key={g.id} game={g} teams={teams} />)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        {(show("top_scorers") || show("tournaments_sidebar")) && (
          <aside className="space-y-8">
            {show("top_scorers") && (
              <div className="bg-navy-deep border border-navy-border rounded-sm overflow-hidden">
                <div className="px-4 py-3 bg-navy-surface flex items-center gap-2 border-b border-navy-border">
                  <BarChart3 size={16} className="text-orange" />
                  <h3 className="font-heading uppercase tracking-widest text-sm">{t("home.top_scorers")}</h3>
                </div>
                <div className="divide-y divide-navy-border">
                  {leaders.length === 0 ? (
                    <div className="px-4 py-6 text-center text-ink-muted text-sm">{t("home.no_data")}</div>
                  ) : leaders.map((l, i) => (
                    <Link key={l.player.id} to={`/players/${l.player.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-navy-surface/50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`font-heading font-black text-xl tabular ${i === 0 ? "text-orange" : "text-ink-muted"}`}>{i + 1}</span>
                        <div className="min-w-0">
                          <div className="font-heading uppercase tracking-tight truncate text-white">{l.player.full_name}</div>
                          <div className="text-xs text-ink-muted truncate">{l.team?.name || ""}</div>
                        </div>
                      </div>
                      <span className="font-mono tabular text-orange font-bold">{l.stats.pts}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {show("tournaments_sidebar") && (
              <div className="bg-navy-deep border border-navy-border rounded-sm overflow-hidden">
                <div className="px-4 py-3 bg-navy-surface flex items-center gap-2 border-b border-navy-border">
                  <Trophy size={16} className="text-orange" />
                  <h3 className="font-heading uppercase tracking-widest text-sm">{t("home.tournaments_side")}</h3>
                </div>
                <div className="divide-y divide-navy-border">
                  {tournaments.length === 0 ? (
                    <div className="px-4 py-6 text-center text-ink-muted text-sm">{t("home.no_tournaments")}</div>
                  ) : tournaments.slice(0, 5).map((tour) => (
                    <Link key={tour.id} to={`/t/${tour.slug || tour.id}`} className="px-4 py-3 flex items-center justify-between hover:bg-navy-surface/50 transition-colors">
                      <div className="min-w-0">
                        <div className="font-heading uppercase tracking-tight truncate text-white text-sm">{tour.name}</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{tour.season || ""}</div>
                      </div>
                      <Clock size={14} className="text-orange" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        )}
      </section>
    </div>
  );
}
